import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Input,
  ViewEncapsulation,
  SimpleChanges,
  Output,
} from "@angular/core";
import { Observable } from "rxjs";
import * as Highcharts from "highcharts";
import {
  BarChartOptions,
  ChartStateColors,
  GlobalHighchartOptions,
  NoDataMsg,
  StackChartOptions,
} from "../pharma-page.model";
import { formatCell } from "src/app/utils/common";
import { PharmaDataService } from "../pharma-data.service";
import {
  VAIconRegistryService,
  infoCircle,
  download,
} from "@va/core-library/icons";
import { ActivatedRoute } from "@angular/router";
@Component({
  selector: "app-drug-sales-analysis-summary-section",
  templateUrl: "./drug-sales-analysis-summary-section.component.html",
  styleUrls: ["./drug-sales-analysis-summary-section.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DrugSalesAnalysisSummarySectionComponent implements OnInit {
  loading = true;
  showError = false;
  chartReady = false;
  dataObservable: Observable<any>;
  chartDataObservable: Observable<any>;
  drugType = "";
  cTagId: number = null;
  highcharts = Highcharts;
  barChartOptions = BarChartOptions;
  stackChartOptions = StackChartOptions;
  downloadOptVisible = false;
  salesAnalysisData;
  customSkeletonConfig = {
    skeletonType: "list",
    config: {
      list: {
        noOfListItems: 12,
        styleUl: {
          height: "510px",
        },
      },
    },
  };
  errorMessage = NoDataMsg;
  @Input() dataSettings;

  chart: Highcharts.Chart;
  chartName = "Test chart";
  updateFlag = false;
  ticker = "";
  @Output() onChartInstance: EventEmitter<Highcharts.Chart> =
    new EventEmitter();
  constructor(
    private pharmaService: PharmaDataService,
    private iconRegistryService: VAIconRegistryService,
    private route: ActivatedRoute,
  ) {
    Highcharts.setOptions(GlobalHighchartOptions);
    this.iconRegistryService.registerIcons([infoCircle, download]);
  }
  @HostListener("window:resize", [])
  sizeChange() {
    this.updateFlag = true;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
    }
  }
  ngOnInit() {
    this.route.params.subscribe((param) => {
      this.cTagId = parseInt(param.cTagId, 10);
      const chartPayload = {
        ctagid: this.cTagId,
        currency: this.dataSettings.currency,
        period: this.dataSettings.periodData.period,
        period_type: this.dataSettings.periodData.period_type,
        period_basis: this.dataSettings.periodData.period_basis,
      };
      this.chartDataObservable =
        this.pharmaService.getDrugSalesChartData(chartPayload);
    });
    this.dataObservable = this.pharmaService.getDrugPageSummarySection(
      this.cTagId,
      this.dataSettings.currency,
    );
  }
  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.dataSettings &&
      changes.dataSettings.currentValue &&
      changes.dataSettings.currentValue.periodData
    ) {
      const dataSettings = changes.dataSettings.currentValue;
      const chartPayload = {
        ctagid: this.cTagId,
        currency: dataSettings.currency === "Base" ? "" : dataSettings.currency,
        period: dataSettings.periodData.period,
        period_type: dataSettings.periodData.period_type,
        period_basis: dataSettings.periodData.period_basis,
      };
      this.showError = false;
      this.chartReady = false;
      this.chartDataObservable =
        this.pharmaService.getDrugSalesChartData(chartPayload);
      this.dataObservable = this.pharmaService.getDrugPageSummarySection(
        this.cTagId,
        dataSettings.currency,
      );
    }
  }

  onHighchartsChartInstance(chartInstance) {
    this.chart = chartInstance;
  }
  onDrugSalesDataResolved(response) {
    this.salesAnalysisData = response;
  }

  onError(error) {
    this.errorMessage = error.message;
    this.showError = true;
    return;
  }
  onDrugSalesChartDataResolved(response) {
    this.drugType = response.drug_type;
    this.setDataForBarChart(response);
    // if (this.drugType.toLowerCase() === "marketed") {
    //   this.setDataForBarChart(response);
    // } else {
    //   this.setDataForStackChart(response);
    // }
  }
  setDataForBarChart(dataObj) {
    if (dataObj.data.length === 0) {
      this.barChartOptions.xAxis["categories"] = [];
      this.barChartOptions.series[0]["data"] = [];
      this.errorMessage = NoDataMsg;
      this.chartReady = true;
      this.showError = true;
      return;
    }
    this.ticker = dataObj.ticker ? dataObj.ticker : "";
    this.barChartOptions.xAxis["categories"] = dataObj.data.map((val) => {
      if (dataObj.period_data[val[0]].is_forecast) {
        return `${val[0]}E`;
      }
      return val[0];
    });
    this.barChartOptions.yAxis["title"].text =
      ` Consensus/VA Actuals (${dataObj.meta_data.currency}${dataObj.meta_data.magnitude}) `;
    this.barChartOptions.series[0]["data"] = dataObj.data.map(
      (point, index) => {
        const period = point[0];
        const name = period === dataObj.peak_year ? "Peak Year" : "";
        const color = dataObj.period_data[period].is_forecast
          ? ChartStateColors.futurePeriod
          : ChartStateColors.historicPeriod;
        const pointObj = {
          x: index,
          y: point[1],
          name: name,
          color: color,
        };
        return pointObj;
      },
    );
    const dataRef = this.formatCellValue;
    this.barChartOptions.tooltip = {
      useHTML: true,
      backgroundColor: "rgba(255,255,255,1)",
      formatter: function () {
        const period = this.point.category.includes("E")
          ? this.point.category.slice(0, this.point.category.length - 1)
          : this.point.category;
        const periodData = dataObj.period_data[period];
        const metaObj = {
          ...dataObj.meta_data,
          currency: "",
        };
        let formattedAmount = dataRef(metaObj, this.point.y);

        formattedAmount =
          dataObj.meta_data.currency +
          dataObj.meta_data.magnitude +
          formattedAmount;
        let tooltipHtml = `<span>${
          periodData.is_forecast ? "Consensus" : "VA Actuals"
        } : ${formattedAmount}`;
        tooltipHtml += "</span><br/>";
        if (periodData && periodData.yoy !== void 0) {
          tooltipHtml += `<span>YOY Growth: ${
            periodData.yoy !== null
              ? `${periodData.yoy}${
                  dataObj.meta_data.unit === "%" ? "bps" : "%"
                }`
              : "0%"
          }</span>`;
        }

        return tooltipHtml;
      },
    };
    this.barChartOptions.exporting.filename = `Trend-${dataObj.drug_name}-Chart`;
    this.barChartOptions.exporting.chartOptions.title.text = `Trend|${dataObj.drug_name}|${this.ticker}`;
    this.chartName = `Trend|${dataObj.drug_name}|${this.ticker}`;
    this.chartReady = true;
  }
  setDataForStackChart(dataObj) {
    this.stackChartOptions.xAxis["categories"] = dataObj.periods;
    this.stackChartOptions.series = dataObj.data.map((val) => {
      return {
        name: val.Name,
        data: [...val.data],
      };
    });
    const callCellFormat = this.formatCellValue;
    const callYoyFormat = this.formatYoyValue;
    this.stackChartOptions.tooltip = {
      useHTML: true,
      formatter: function () {
        let tooltipHtml = `<span>${this.series.name} : ${callCellFormat(
          dataObj.meta_data,
          this.point.y,
        )}`;
        const periodData = dataObj.period_data[this.point.category];
        tooltipHtml += "</span><br/>";
        const yoy =
          this.series.name === "Gross Revenue"
            ? periodData.gross_revenue_yoy
            : periodData.total_revenue_yoy;
        if (periodData && yoy) {
          tooltipHtml += `<span>YOY Growth ${
            dataObj.meta_data.currency
          } : ${callYoyFormat(dataObj.meta_data, yoy)}
          </span>`;
        }
        return tooltipHtml;
      },
    };
    this.stackChartOptions.exporting.filename = `Trend-${dataObj.drug_name}-Chart`;
    this.stackChartOptions.exporting.chartOptions.title.text = `Trend|${dataObj.drug_name}|${this.ticker}`;
    this.chartReady = true;
  }
  formatCellValue(metaData, value) {
    const newVal = formatCell(
      value,
      metaData.currency,
      metaData.unit,
      "Amount",
    );
    return newVal;
  }
  formatYoyValue(metaData, value) {
    const newVal = formatCell(value, metaData.currency, metaData.unit, "yoy");
    return newVal;
  }

  downloadValue(downloadType: number) {
    this.downloadOptVisible = false;

    // Check if the chart property is defined before accessing its properties
    if (this.chart) {
      this.chart.options.title.text = this.chartName;
      switch (downloadType) {
        case 0:
          this.chart.exportChart(
            {
              type: "image/png",
              filename: this.barChartOptions.exporting.filename,
              sourceWidth: 1920,
              sourceHeight: 1080,
            },
            this.chart.options,
          );
          break;
        case 2:
          this.chart.exportChart(
            {
              type: "application/pdf",
              filename: this.barChartOptions.exporting.filename,
              sourceWidth: 1920,
              sourceHeight: 1080,
            },
            this.chart.options,
          );
          break;
        default:
          break;
      }
    }
  }
}
